/**
 * @file autoSuggestions.tsx
 *
 * @fileoverview Exports the auto suggestions feature related utils.
 */
import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import TaxonomyReferenceMultiple from '~/jobiqo/components/Form/Widgets/TaxonomyReference/TaxonomyReferenceMultiple';
import TextLongWidget from '~/jobiqo/components/Form/Widgets/TextLong';
import AutoSuggestions from '~/jobiqo/components/Job/Form/components/AutoSuggestion';
import GET_AUTOSUGGESTIONS from '~/jobiqo/components/Job/graphql/queries/getAutoSuggestions.gql';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { AutoSuggestion } from '~/jobiqo/models/jobs';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
/**
 * It returns an object with overrides for the job form fields related
 * to the auto suggestions feature.
 * @returns An object with keys that match the field names of the job form fields
 * that are related to the auto suggestions feature.
 */
export function getAutoSuggestionJobFormOverrides() {
  const jobsConfig = getCurrentFrontendConfig().JOBS || {};
  if (!jobsConfig.AUTOSUGGESTIONS_ENABLED) {
    return null;
  }

  const AUTOSUGGESTIONS_AUTOFILL_FIELDS =
    jobsConfig.AUTOSUGGESTIONS_AUTOFILL_FIELDS;
  const [suggestions, setSuggestions] = useState<AutoSuggestion[]>([]);
  const [getAutoSuggestions, {}] = useLazyQuery(GET_AUTOSUGGESTIONS);
  const [autoFilled, setAutoFilled] = useState(false);

  async function handleOnBlur(values) {
    const title = values?.title;
    const description = values?.field_job_description;

    if (title && description) {
      try {
        const response = await getAutoSuggestions({
          variables: {
            title: title,
            description: description,
          },
        });

        if (suggestions !== response?.data?.getAutoSuggestions) {
          setAutoFilled(false);
        }
        setSuggestions(response?.data?.getAutoSuggestions);
      } catch (e) {
        console.error('Failed to get any autosuggestions data.');
      }
    }
  }

  function handleSuggestionClick(
    values,
    fieldName: string,
    chipId: string,
    chipName: string,
    setFieldValue,
  ) {
    const newSuggestion = { tid: chipId, name: chipName };

    values[fieldName].push(newSuggestion);

    setFieldValue(fieldName, [...removeDuplicates(values[fieldName])]);
  }

  function autoFillField(
    fieldName: string,
    setFieldValue,
    values,
    suggestions,
  ) {
    if (
      AUTOSUGGESTIONS_AUTOFILL_FIELDS.includes(fieldName) &&
      suggestions &&
      !autoFilled
    ) {
      const fieldValues = values[fieldName];
      suggestions.forEach((value) => {
        if (!fieldValues.includes(value)) {
          fieldValues.push({ tid: value.tid.toString(), name: value.label });
        }
      });

      setFieldValue(fieldName, [...removeDuplicates(values[fieldName])]);
      setAutoFilled(true);
    }
  }

  function removeDuplicates(fieldValues) {
    return fieldValues.filter(
      (item, index, self) =>
        index === self.findIndex((obj) => obj.tid === item.tid),
    );
  }

  return {
    // Override to set up an onBlur event that fires for getting the suggestions.
    field_job_description: ({ field, values }) => {
      return (
        <div key={field.id}>
          <TextLongWidget
            entityField={field}
            onBlur={() => handleOnBlur(values)}
          />
          <Spacing size="medium" />
        </div>
      );
    },
    field_job_employment_type: ({ field, values, setFieldValue }) => {
      const employment_type = suggestions?.find(
        (suggestion) => suggestion.name === 'employment_type',
      );
      if (employment_type) {
        // Autofill the field with the suggestions, if enabled in the config.
        autoFillField(
          'field_job_employment_type',
          setFieldValue,
          values,
          employment_type.autofill,
        );
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple
              required={true}
              entityField={field}
              refresh={suggestions}
            />
            <Spacing size="small" />
            <div>
              <AutoSuggestions
                suggestions={employment_type.terms}
                onClick={(fieldName, chipId, chipName) =>
                  handleSuggestionClick(
                    values,
                    fieldName,
                    chipId,
                    chipName,
                    setFieldValue,
                  )
                }
                fieldName={field.id}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple required={true} entityField={field} />
            <Spacing size="small" />
          </div>
        );
      }
    },
    field_job_occupational_field: ({ field, values, setFieldValue }) => {
      const occupational_fields = suggestions?.find(
        (suggestion) => suggestion.name === 'occupational_fields',
      );
      if (occupational_fields) {
        // Autofill the field with the suggestions, if enabled in the config.
        autoFillField(
          'field_job_occupational_field',
          setFieldValue,
          values,
          occupational_fields.autofill,
        );
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple
              required={true}
              entityField={field}
              refresh={suggestions}
            />
            <Spacing size="small" />
            <div>
              <AutoSuggestions
                suggestions={occupational_fields.terms}
                onClick={(fieldName, chipId, chipName) =>
                  handleSuggestionClick(
                    values,
                    fieldName,
                    chipId,
                    chipName,
                    setFieldValue,
                  )
                }
                fieldName={field.id}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple required={true} entityField={field} />
            <Spacing size="small" />
          </div>
        );
      }
    },
    field_job_fields_of_study: ({ field, values, setFieldValue }) => {
      const fields_of_study = suggestions?.find(
        (suggestion) => suggestion.name === 'fields_of_study',
      );
      if (fields_of_study) {
        // Autofill the field with the suggestions, if enabled in the config.
        autoFillField(
          'field_job_fields_of_study',
          setFieldValue,
          values,
          fields_of_study.autofill,
        );
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple
              required={true}
              entityField={field}
              refresh={suggestions}
            />
            <Spacing size="small" />
            <div>
              <AutoSuggestions
                suggestions={fields_of_study.terms}
                onClick={(fieldName, chipId, chipName) =>
                  handleSuggestionClick(
                    values,
                    fieldName,
                    chipId,
                    chipName,
                    setFieldValue,
                  )
                }
                fieldName={field.id}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple required={true} entityField={field} />
            <Spacing size="small" />
          </div>
        );
      }
    },
    field_job_skills: ({ field, values, setFieldValue }) => {
      const skills = suggestions?.find(
        (suggestion) => suggestion.name === 'skills',
      );
      if (skills) {
        // Autofill the field with the suggestions, if enabled in the config.
        autoFillField(
          'field_job_skills',
          setFieldValue,
          values,
          skills.autofill,
        );
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple
              required={true}
              entityField={field}
              refresh={suggestions}
            />
            <Spacing size="small" />
            <div>
              <AutoSuggestions
                suggestions={skills.terms}
                onClick={(fieldName, chipId, chipName) =>
                  handleSuggestionClick(
                    values,
                    fieldName,
                    chipId,
                    chipName,
                    setFieldValue,
                  )
                }
                fieldName={field.id}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div key={field.id}>
            <TaxonomyReferenceMultiple required={true} entityField={field} />
            <Spacing size="small" />
          </div>
        );
      }
    },
  };
}
